import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { restaurantAPI } from '../lib/api';

const LocationContext = createContext(null);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!user) {
      setLocations([]);
      setLoading(false);
      return;
    }
    try {
      const res = await restaurantAPI.getAll();
      setLocations(res.data || []);
    } catch {
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Restore persisted selection after locations load
  useEffect(() => {
    if (locations.length === 0) return;
    const saved = localStorage.getItem('restaff_selected_location');
    if (saved === 'all' && (user?.role === 'owner' || user?.role === 'super_admin')) {
      setSelectedLocationId('all');
    } else if (saved && locations.some(l => l.id === saved)) {
      setSelectedLocationId(saved);
    } else if (user?.restaurant_id && locations.some(l => l.id === user.restaurant_id)) {
      setSelectedLocationId(user.restaurant_id);
    } else if (locations.length > 0) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, user]);

  const setSelectedLocation = (id) => {
    setSelectedLocationId(id);
    localStorage.setItem('restaff_selected_location', id);
  };

  const selectedLocation = selectedLocationId === 'all'
    ? { id: 'all', location_name: 'All Locations' }
    : locations.find(l => l.id === selectedLocationId) || null;

  const isAllLocations = selectedLocationId === 'all';
  const canSwitchLocation = user?.role === 'owner' || user?.role === 'manager' || user?.role === 'super_admin';
  const canSelectAll = user?.role === 'owner' || user?.role === 'super_admin';

  return (
    <LocationContext.Provider value={{
      locations,
      selectedLocation,
      selectedLocationId,
      setSelectedLocation,
      isAllLocations,
      canSwitchLocation,
      canSelectAll,
      loading,
      refreshLocations: fetchLocations,
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
